import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import mermaid from 'mermaid';
import googleDriveService from '../services/googleDrive';

// Initialize Mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  themeVariables: {
    primaryColor: '#f4f4f5',
    primaryTextColor: '#000',
    primaryBorderColor: '#7c7c7c',
    lineColor: '#5c5c5c',
    secondaryColor: '#e0e0e0',
    tertiaryColor: '#f0f0f0',
    background: '#ffffff',
    mainBkg: '#ffffff',
    secondBkg: '#f4f4f5',
    tertiaryBkg: '#e0e0e0',
    primaryBorderColor: '#cccccc',
    lineColor: '#333333',
    fontFamily: '"Helvetica Neue", Arial, sans-serif',
    fontSize: '16px',
    textColor: '#333',
    labelTextColor: '#000000',
    nodeTextColor: '#000000',
    nodeBkg: '#f9f9f9',
    nodeBorder: '#cccccc',
    clusterBkg: '#ffffde',
    clusterBorder: '#aaaa33',
    defaultLinkColor: '#333333',
    titleColor: '#333333',
    edgeLabelBackground: '#ffffff',
    actorBorder: '#cccccc',
    actorBkg: '#f9f9f9',
    actorTextColor: '#000000',
    actorLineColor: '#333333',
    signalColor: '#333333',
    signalTextColor: '#333333',
    labelBoxBkgColor: '#f9f9f9',
    labelBoxBorderColor: '#cccccc',
    labelTextColor: '#333333',
    loopTextColor: '#333333',
    noteBorderColor: '#cccccc',
    noteBkgColor: '#ffffcc',
    noteTextColor: '#333333',
    activationBorderColor: '#666666',
    activationBkgColor: '#f4f4f4',
    sequenceNumberColor: '#ffffff'
  },
  securityLevel: 'loose',
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    rankSpacing: 50,
    nodeSpacing: 50,
    curve: 'basis'
  }
});

// Mermaid component
const MermaidDiagram = ({ chart, id }) => {
  const ref = useRef(null);
  
  useEffect(() => {
    if (ref.current && chart) {
      try {
        ref.current.innerHTML = '';
        mermaid.render(`mermaid-${id}-${Date.now()}`, chart).then(({ svg }) => {
          if (ref.current) {
            ref.current.innerHTML = svg;
          }
        });
      } catch (error) {
        console.error('Mermaid rendering error:', error);
        if (ref.current) {
          ref.current.innerHTML = `<div style="color: red; padding: 10px; border: 1px solid red; border-radius: 4px;">
            <strong>Mermaid Diagram Error:</strong><br/>
            <code>${chart}</code>
          </div>`;
        }
      }
    }
  }, [chart, id]);
  
  return (
    <div 
      ref={ref} 
      className="mermaid-diagram" 
      style={{ 
        textAlign: 'center', 
        margin: '20px 0',
        padding: '20px',
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'auto'
      }} 
    />
  );
};

const MarkdownViewer = ({ document }) => {
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const contentRef = useRef(null);

  useEffect(() => {
    if (document) {
      loadMarkdownContent();
    }
  }, [document]);

  const loadMarkdownContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const accessToken = await googleDriveService.getAccessToken();
      
      // Fetch the file content from Google Drive
      const response = await fetch(`https://www.googleapis.com/drive/v3/files/${document.id}?alt=media`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load markdown file: ${response.statusText}`);
      }

      const content = await response.text();
      setMarkdownContent(content);
      setEditContent(content);
    } catch (err) {
      console.error('Error loading markdown:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditContent(markdownContent);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const accessToken = await googleDriveService.getAccessToken();
      
      // Use simple upload method with shared drive support
      const params = new URLSearchParams({
        uploadType: 'media',
        supportsAllDrives: 'true'
      });
      
      const response = await fetch(`https://www.googleapis.com/upload/drive/v3/files/${document.id}?${params}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'text/plain'
        },
        body: editContent
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Save error response:', errorText);
        throw new Error(`${response.status} ${response.statusText}: ${errorText}`);
      }

      setMarkdownContent(editContent);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving markdown:', err);
      alert(`Failed to save: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditContent(markdownContent);
  };

  if (loading) {
    return (
      <div className="markdown-viewer">
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading {document.name}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="markdown-viewer">
        <div className="error-indicator">
          <p>Failed to load {document.name}</p>
          <p>{error}</p>
          <a 
            href={`https://drive.google.com/file/d/${document.id}/view`}
            target="_blank"
            rel="noopener noreferrer"
            className="direct-link"
          >
            Open in Google Drive
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="markdown-viewer">
      <div className="markdown-header">
        <h1 className="document-title">{document.name.replace(/\.md$/i, '')}</h1>
        {!isEditing && (
          <button onClick={handleEdit} className="edit-button-inline">
            Edit
          </button>
        )}
        {isEditing && (
          <div className="edit-actions">
            <button onClick={handleSave} className="save-button" disabled={loading}>
              Save
            </button>
            <button onClick={handleCancel} className="cancel-button">
              Cancel
            </button>
          </div>
        )}
      </div>
      
      <div className="markdown-content">
        <div className="markdown-content-wrapper" ref={contentRef}>
          {isEditing ? (
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="markdown-editor"
              placeholder="Write your markdown content here..."
            />
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  const language = match ? match[1] : '';
                  const code = String(children).replace(/\n$/, '');
                  
                  // Check if this is a Mermaid diagram
                  if (!inline && (language === 'mermaid' || code.trim().startsWith('graph') || code.trim().startsWith('flowchart') || code.trim().startsWith('sequenceDiagram') || code.trim().startsWith('classDiagram'))) {
                    return <MermaidDiagram chart={code} id={Math.random().toString(36).substr(2, 9)} />;
                  }
                  
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={tomorrow}
                      language={language}
                      PreTag="div"
                      {...props}
                    >
                      {code}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                },
                // Custom styling for headings with IDs for table of contents
                h1({ children }) {
                  const text = children.toString();
                  const id = text.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
                  return <h1 id={id}>{children}</h1>;
                },
                h2({ children }) {
                  const text = children.toString();
                  const id = text.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
                  return <h2 id={id}>{children}</h2>;
                },
                h3({ children }) {
                  const text = children.toString();
                  const id = text.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
                  return <h3 id={id}>{children}</h3>;
                },
                h4({ children }) {
                  const text = children.toString();
                  const id = text.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
                  return <h4 id={id}>{children}</h4>;
                },
                h5({ children }) {
                  const text = children.toString();
                  const id = text.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
                  return <h5 id={id}>{children}</h5>;
                },
                h6({ children }) {
                  const text = children.toString();
                  const id = text.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
                  return <h6 id={id}>{children}</h6>;
                },
                // Custom styling for tables
                table({ children }) {
                  return <table className="markdown-table">{children}</table>;
                },
                // Custom styling for blockquotes with type detection and icons
                blockquote({ children }) {
                  // Better text extraction from React children
                  const extractText = (element) => {
                    if (typeof element === 'string') return element;
                    if (typeof element === 'number') return String(element);
                    if (Array.isArray(element)) return element.map(extractText).join('');
                    if (element?.props?.children) return extractText(element.props.children);
                    return '';
                  };
                  
                  const text = extractText(children).toLowerCase();
                  console.log('Extracted blockquote text:', text);
                  
                  // Determine alert type and styling
                  let alertType = null;
                  let icon = '';
                  let bgColor = '#f8f9fa';
                  let borderColor = '#e6e6e6';
                  let textColor = '#333';
                  let titleColor = '#333';
                  
                  if (text.includes('warning')) {
                    alertType = 'WARNING';
                    icon = '⚠️';
                    bgColor = '#fffbeb';
                    borderColor = '#f59e0b';
                    textColor = '#000';
                    titleColor = '#d97706';
                  } else if (text.includes('critical')) {
                    alertType = 'CRITICAL';
                    icon = '🚨';
                    bgColor = '#fed7d7';
                    borderColor = '#c53030';
                    textColor = '#000';
                    titleColor = '#c53030';
                  } else if (text.includes('important')) {
                    alertType = 'IMPORTANT';
                    icon = 'ℹ️';
                    bgColor = '#f0f5ff';
                    borderColor = '#0052cc';
                    textColor = '#000';
                    titleColor = '#0052cc';
                  } else if (text.includes('note')) {
                    alertType = 'NOTE';
                    icon = '📝';
                    bgColor = '#f7fafc';
                    borderColor = '#718096';
                    textColor = '#000';
                    titleColor = '#718096';
                  } else if (text.includes('tip')) {
                    alertType = 'TIP';
                    icon = '💡';
                    bgColor = '#f0fff4';
                    borderColor = '#38a169';
                    textColor = '#000';
                    titleColor = '#38a169';
                  }
                  
                  console.log('Alert type detected:', alertType);
                  
                  return (
                    <div 
                      style={{
                        margin: '20px 0',
                        padding: '16px 20px',
                        borderLeft: `4px solid ${borderColor}`,
                        background: bgColor,
                        borderRadius: '0 8px 8px 0',
                        color: textColor,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      {alertType && (
                        <div style={{ 
                          fontWeight: 'bold', 
                          color: titleColor, 
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span style={{ fontSize: '18px' }}>{icon}</span>
                          <span>{alertType}</span>
                        </div>
                      )}
                      <div style={{ color: textColor }}>
                        {(() => {
                          // Get the full text and extract everything AFTER the colon
                          const fullText = extractText(children);
                          console.log('Full text before cleaning:', fullText);
                          
                          // Simple approach: find first colon and take everything after it
                          if (fullText.includes(':')) {
                            const parts = fullText.split(':');
                            const afterColon = parts.slice(1).join(':').trim(); // Join back in case there are multiple colons
                            console.log('Text after colon:', afterColon);
                            return afterColon || fullText;
                          }
                          
                          return fullText;
                        })()}
                      </div>
                    </div>
                  );
                },
                // Custom styling for links
                a({ href, children }) {
                  // Handle internal anchor links (table of contents)
                  if (href && href.startsWith('#')) {
                    return (
                      <a 
                        href={href}
                        className="markdown-link"
                        onClick={(e) => {
                          e.preventDefault();
                          if (contentRef.current) {
                            const target = contentRef.current.querySelector(href);
                            if (target) {
                              target.scrollIntoView({ behavior: 'smooth' });
                            }
                          }
                        }}
                      >
                        {children}
                      </a>
                    );
                  }
                  // External links open in new tab
                  return (
                    <a 
                      href={href} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="markdown-link"
                    >
                      {children}
                    </a>
                  );
                }
              }}
            >
              {markdownContent}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarkdownViewer;